import { useEffect, useState, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import Pipeline from "@/pages/Pipeline";
import PlansPage from "@/pages/PlansPage";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Templates from "@/pages/Templates";
import Workflows from "@/pages/Workflows";
import KnowledgeBase from "@/pages/KnowledgeBase";
import BrandManager from "@/pages/BrandManager";
import BrandAdmin from "@/pages/BrandAdmin";
import BrandAssetGallery from "@/pages/BrandAssetGallery";
import StockSearch from "@/pages/StockSearch";
import TriageMode from "@/pages/TriageMode";
import DuplicatesMode from "@/pages/DuplicatesMode";
import AdminConsole from "@/pages/AdminConsole";
import DataHygienePage from "@/pages/DataHygienePage";
import Research from "@/pages/Research";
import Campaigns from "@/pages/Campaigns";
import Sequences from "@/pages/Sequences";
import UserManagement from "@/pages/UserManagement";
import ProposalGenerator from "@/pages/ProposalGenerator";
import Presentations from "@/pages/Presentations";
import AskRex from "@/pages/AskRex";
import LeadershipBrief from "@/pages/LeadershipBrief";
import Perspectives from "@/pages/Perspectives";
import OpportunityRadar from "@/pages/OpportunityRadar";
import ExecutionBoard from "@/pages/ExecutionBoard";
import ScrumManagement from "@/pages/ScrumManagement";
import MPDashboard from "@/pages/MPDashboard";
import CommercialDashboard from "@/pages/CommercialDashboard";
import EntryIntelligence from "@/pages/EntryIntelligence";
import CommercialBriefs from "@/pages/CommercialBriefs";
import PatternReview from "@/pages/PatternReview";
import TeamSubmit from "@/pages/TeamSubmit";
import DesignerUploadPortal from "@/pages/DesignerUploadPortal";
import DesignerGuide from "@/pages/DesignerGuide";
import ActionsPage from "@/pages/ActionsPage";
import RexInbox from "@/pages/RexInbox";
import EventRadar from "@/pages/EventRadar";
import TenderRadar from "@/pages/TenderRadar";
import ContentStudioPage from "@/pages/ContentStudioPage";
import MissionControlPage from "@/pages/MissionControlPage";
import MorningScrumOS from "@/pages/MorningScrumOS";
import ExecutiveCommandCentre from "@/pages/ExecutiveCommandCentre";
import ProposalStudioM0 from "@/pages/ProposalStudioM0";
import ProductionPlanner from "@/pages/ProductionPlanner";
import UserGuide from "@/pages/UserGuide";
import GrowPage from "@/pages/GrowPage";
import AmplifyPage from "@/pages/AmplifyPage";
import LensBoard from "@/pages/LensBoard";
import OneList from "@/pages/OneList";
import PreviewEmails from "@/pages/PreviewEmails";
import PricingSheets from "@/pages/PricingSheets";
import Tasks from "@/pages/Tasks";
import Nurture from "@/pages/Nurture";
import DeliveryShadow from "@/pages/DeliveryShadow";
import WeeklyAgenda from "@/pages/WeeklyAgenda";
import Recommendations from "@/pages/Recommendations";
import IntegrationHealth from "@/pages/IntegrationHealth";
import AwaitingReplies from "@/pages/AwaitingReplies";
import WaitsTriage from "@/pages/WaitsTriage";
import EngagementsPage from "@/pages/EngagementsPage";
import FxRatesPage from "@/pages/FxRatesPage";
// RevenuePage now lives inside CommercialDashboard as the "Revenue & Forecast" tab.
import NotificationPrefsPage from "@/pages/NotificationPrefsPage";
import CalibrationView from "@/pages/CalibrationView";
import VlmCriticDigest from "@/pages/VlmCriticDigest";
import Phase6ModelComparison from "@/pages/Phase6ModelComparison";
import LegacyBanner from "@/components/LegacyBanner";
import Layout from "@/components/Layout";
import { WorkspaceProvider } from "@/lib/workspaceContext";
import { ClientDrawerProvider } from "@/components/ClientDrawerProvider";
import "@/App.css";
import { API } from "@/lib/constants";

// Auth Callback Component - handles session_id from OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (response.ok) {
          const user = await response.json();
          navigate("/morning-scrum", { state: { user } });
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  // Ported from parallel fork's iter-243 fix (May 2026): instead of
  // bouncing the user to /login when the backend has a transient 5xx
  // wobble, flip into a "reconnecting" state, show an amber banner, and
  // silently keep polling /auth/me every 5s in the background. Genuine
  // 401/403 still go straight to /login.
  const [reconnecting, setReconnecting] = useState(false);
  // REX-RECONNECT-UI (May 2026) — show users a "last good at HH:MM" so
  // they can gauge how long the backend has been wobbly, plus an active
  // "Retry now" button so they aren't stuck staring at a passive spinner.
  const [lastGoodAt, setLastGoodAt] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const authCheckedRef = useRef(false);
  const reconnectIntervalRef = useRef(null);

  // Manual retry — short-circuits the silent polling loop. Called by the
  // banner's "Retry now" button.
  const retryNow = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const r = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (r.ok) {
        const userData = await r.json();
        setUser(userData);
        setIsAuthenticated(true);
        setReconnecting(false);
        setLastGoodAt(new Date());
        if (reconnectIntervalRef.current) {
          clearInterval(reconnectIntervalRef.current);
          reconnectIntervalRef.current = null;
        }
      } else if (r.status === 401 || r.status === 403) {
        if (reconnectIntervalRef.current) {
          clearInterval(reconnectIntervalRef.current);
          reconnectIntervalRef.current = null;
        }
        setIsAuthenticated(false);
        navigate("/login");
      }
      // Other status → stay in reconnecting state, keep the timestamp.
    } catch {
      // Stay in reconnecting state.
    } finally {
      setRetrying(false);
    }
  }, [retrying, navigate]);

  useEffect(() => {
    // If already authenticated, don't re-check
    if (isAuthenticated === true && user) {
      return;
    }

    // If user data passed from AuthCallback/Login, use it
    if (location.state?.user && !authCheckedRef.current) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      authCheckedRef.current = true;
      return;
    }

    // Only check auth once
    if (authCheckedRef.current) {
      return;
    }

    const checkAuth = async () => {
      // Production stability fix (Feb 2026): retry up to 6 times with
      // exponential backoff (1s, 2s, 4s, 8s, 8s, 8s ≈ 31s total) so a
      // Neon Postgres cold-start cascading 5xx onto unrelated MongoDB
      // endpoints can't forcibly log the user out mid-session.
      // Only 401/403 short-circuit immediately — everything else gets
      // the full backoff window. 503 with Retry-After is also retried.
      const maxAttempts = 6;
      let lastStatus = 0;
      let signedOut = false;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            credentials: "include",
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setIsAuthenticated(true);
            setReconnecting(false);
            setLastGoodAt(new Date());
            return;
          }

          lastStatus = response.status;
          // 401/403 = genuinely not authenticated, don't retry
          if (response.status === 401 || response.status === 403) {
            signedOut = true;
            break;
          }

          // Other errors (500/502/503/504, network) = retry with backoff
          if (attempt < maxAttempts - 1) {
            const backoffMs = Math.min(1000 * Math.pow(2, attempt), 8000);
            console.warn(`Auth retry ${attempt + 1}/${maxAttempts} after ${backoffMs}ms (status ${response.status})`);
            await new Promise(r => setTimeout(r, backoffMs));
            continue;
          }
        } catch (error) {
          // Network failure (DNS, connection reset, CORS, offline)
          console.error(`Auth check attempt ${attempt + 1} error:`, error);
          if (attempt < maxAttempts - 1) {
            const backoffMs = Math.min(1000 * Math.pow(2, attempt), 8000);
            await new Promise(r => setTimeout(r, backoffMs));
            continue;
          }
        }
      }

      authCheckedRef.current = true;
      if (signedOut) {
        // Real auth failure (401/403) — redirect to login as before.
        setIsAuthenticated(false);
        navigate("/login");
        return;
      }
      // All attempts failed with 5xx/network — DON'T bounce the user.
      // Keep their session UI rendered (isAuthenticated stays null), show
      // an amber reconnecting banner, and silently keep polling.
      console.warn(`Auth check exhausted retries (last status ${lastStatus}) — entering reconnecting state`);
      setReconnecting(true);
      if (!reconnectIntervalRef.current) {
        reconnectIntervalRef.current = setInterval(async () => {
          try {
            const r = await fetch(`${API}/auth/me`, { credentials: "include" });
            if (r.ok) {
              const userData = await r.json();
              setUser(userData);
              setIsAuthenticated(true);
              setReconnecting(false);
              setLastGoodAt(new Date());
              clearInterval(reconnectIntervalRef.current);
              reconnectIntervalRef.current = null;
            } else if (r.status === 401 || r.status === 403) {
              clearInterval(reconnectIntervalRef.current);
              reconnectIntervalRef.current = null;
              setIsAuthenticated(false);
              navigate("/login");
            }
          } catch {
            /* keep polling */
          }
        }, 5000);
      }
    };

    checkAuth();

    return () => {
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
    };
  }, [navigate, location.state, isAuthenticated, user]);

  if (isAuthenticated === null && !reconnecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // When we hit the reconnecting state BEFORE the user was ever
  // authenticated (e.g. backend was already down on first page load), we
  // can't render children safely. Show a full-page "reconnecting" splash
  // instead. If the user was authenticated previously and we then went
  // into reconnecting, we render the banner above their existing UI.
  if (reconnecting && isAuthenticated !== true) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mb-4"></div>
        <p className="text-sm font-medium text-amber-900" data-testid="auth-reconnecting-splash">Reconnecting to REX…</p>
        <p className="text-xs text-amber-700 mt-1">Your work is safe. We&apos;ll resume as soon as the backend responds.</p>
        {lastGoodAt && (
          <p className="text-[11px] text-amber-600 mt-3 tabular-nums" data-testid="auth-reconnecting-splash-last-good">
            Last good at {lastGoodAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
        <button
          type="button"
          onClick={retryNow}
          disabled={retrying}
          data-testid="auth-reconnecting-splash-retry"
          className="mt-4 px-4 py-1.5 rounded border border-amber-400 bg-white text-amber-900 hover:bg-amber-50 disabled:opacity-50 text-xs font-medium"
        >
          {retrying ? "Retrying…" : "Retry now"}
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <>
      {reconnecting && (
        <div
          className="fixed top-0 inset-x-0 z-[100] bg-amber-100 border-b border-amber-300 text-amber-900 text-xs px-4 py-2 flex items-center justify-center gap-3 shadow-sm"
          data-testid="auth-reconnecting-banner"
          role="status"
          aria-live="polite"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          <span className="font-medium">Reconnecting to REX</span>
          <span className="text-amber-800 hidden sm:inline">— your work is safe. We&apos;ll resume as soon as the backend responds.</span>
          {lastGoodAt && (
            <span className="text-amber-700 tabular-nums" data-testid="auth-reconnecting-last-good">
              · Last good at {lastGoodAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            onClick={retryNow}
            disabled={retrying}
            data-testid="auth-reconnecting-retry"
            className="ml-2 px-2 py-0.5 rounded border border-amber-400 bg-white text-amber-900 hover:bg-amber-50 disabled:opacity-50 text-[11px] font-medium"
          >
            {retrying ? "Retrying…" : "Retry now"}
          </button>
        </div>
      )}
      {children({ user, setUser })}
    </>
  );
};

// App Router - handles session_id detection synchronously
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/team-submit" element={<TeamSubmit />} />
      <Route path="/d/upload/:token" element={<DesignerUploadPortal />} />
      <Route path="/amplify-guide" element={<DesignerGuide />} />
      <Route
        path="/command-centre"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ExecutiveCommandCentre user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposal-studio-m0"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <ProposalStudioM0 user={user} />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/production-planner"
        element={<Navigate to="/amplify" replace />}
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <Layout user={user}>
                <Profile user={user} setUser={setUser} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <CommercialDashboard user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Pipeline user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/one-list"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <OneList user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/preview-emails"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <PreviewEmails user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pricing"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <PricingSheets user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Tasks user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/nurture"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Nurture user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/delivery"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <DeliveryShadow user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/agenda"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <WeeklyAgenda user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/recommendations"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Recommendations user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/integration-health"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <IntegrationHealth user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <PlansPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans/:planId"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <PlansPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/entry-intelligence"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <EntryIntelligence user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/commercial-briefs"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <CommercialBriefs user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/commercial-briefs/:briefId"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <CommercialBriefs user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/pattern-review"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <PatternReview user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/actions"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ActionsPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/rex-inbox"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <RexInbox user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Clients user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ClientDetail user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Templates user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Workflows user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <KnowledgeBase user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand-manager"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <BrandManager user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <BrandAdmin user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand/:workspaceId"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <BrandAdmin user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/brand-assets"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <BrandAssetGallery user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/brand-assets/stock-search"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <StockSearch user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/brand-assets/triage"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <TriageMode user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/brand-assets/duplicates"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <DuplicatesMode user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/research"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Research user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Campaigns user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sequences"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Sequences user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <AdminConsole user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-hygiene"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <DataHygienePage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <UserManagement user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposals"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ProposalGenerator user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposals/:proposalId"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ProposalGenerator user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/presentations"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Presentations />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ask-rex"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <AskRex user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunity-radar"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <OpportunityRadar user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/event-radar"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <EventRadar user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tender-radar"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <TenderRadar user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/mission-control"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <MissionControlPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/morning-scrum"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <MorningScrumOS user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/guide"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <UserGuide />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/content-studio"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ContentStudioPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/execution-board"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ExecutionBoard user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route path="/commercial-dashboard" element={<Navigate to="/dashboard" />} />
      <Route
        path="/scrum"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <ScrumManagement user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/mp-dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <MPDashboard user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/leadership-brief"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <LeadershipBrief user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/perspectives"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Perspectives user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/today" />} />
      {/* ─── REX v3 — six surfaces (Phase 1) ───────────────── */}
      <Route path="/today" element={<Navigate to="/morning-scrum" replace />} />
      <Route path="/revenue" element={<Navigate to="/dashboard?tab=revenue" replace />} />
      <Route
        path="/grow"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <GrowPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/engagements"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <EngagementsPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/fx-rates"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <FxRatesPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <NotificationPrefsPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/amplify"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <AmplifyPage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/lens"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <LensBoard user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/awaiting-replies"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <AwaitingReplies user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/triage"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <WaitsTriage user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route path="/ask" element={<Navigate to="/ask-rex" replace />} />
      <Route
        path="/workspaces/:workspaceId/critic/calibration"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <CalibrationView user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/:workspaceId/critic/digest"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <VlmCriticDigest />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/phase6/model-comparison"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <Layout user={user}>
                <Phase6ModelComparison user={user} />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/today" />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <WorkspaceProvider>
        <BrowserRouter>
          <ClientDrawerProvider>
            <AppRouter />
          </ClientDrawerProvider>
        </BrowserRouter>
      </WorkspaceProvider>
    </div>
  );
}

export default App;
